<template>
  <div id="layout-wrapper">
    <NavigationBar />
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <!-- Page Header -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18">Appraisal Details - HR View</h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item"><router-link to="/hr-dashboard">HR Dashboard</router-link></li>
                    <li class="breadcrumb-item active">View Details</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2">Loading appraisal details...</p>
          </div>

          <!-- Appraisal Details -->
          <div v-else-if="appraisal">

            <!-- Summary Card with Actions -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card border-success">
                  <div class="card-header bg-success text-white d-flex justify-content-between align-items-center">
                    <h5 class="mb-0 text-white">Complete Appraisal Summary</h5>
                    <div>
                      <button
                          @click="reviewAppraisal"
                          class="btn btn-light btn-sm me-2"
                      >
                        <i class="mdi mdi-check-circle me-1"></i> Final Review
                      </button>
                      <button @click="printAppraisal" class="btn btn-light btn-sm">
                        <i class="mdi mdi-printer me-1"></i> Print
                      </button>
                    </div>
                  </div>
                  <div class="card-body">
                    <div class="row">
                      <div class="col-md-3">
                        <label class="fw-bold text-muted small">Employee Name:</label>
                        <p class="mb-2">{{ appraisal.employeeName }}</p>
                        <label class="fw-bold text-muted small">Position:</label>
                        <p class="mb-2">{{ appraisal.positionTitle }}</p>
                        <label class="fw-bold text-muted small">Department:</label>
                        <p class="mb-0">{{ appraisal.department }}</p>
                      </div>
                      <div class="col-md-3">
                        <label class="fw-bold text-muted small">Review Period:</label>
                        <p class="mb-2">{{ appraisal.periodStart }} - {{ appraisal.periodEnd }}</p>
                        <label class="fw-bold text-muted small">Year:</label>
                        <p class="mb-2">{{ appraisal.appraisalYear }}</p>
                        <label class="fw-bold text-muted small">Status:</label>
                        <p class="mb-0">
                          <span :class="getStatusBadgeClass(appraisal.status)" class="badge">
                            {{ appraisal.status }}
                          </span>
                        </p>
                      </div>
                      <div class="col-md-3">
                        <label class="fw-bold text-muted small">Submitted Date:</label>
                        <p class="mb-2">{{ appraisal.selfAssessmentDate || '-' }}</p>
                        <label class="fw-bold text-muted small">Manager:</label>
                        <p class="mb-2">{{ appraisal.appraiserName || 'Not assigned' }}</p>
                        <label class="fw-bold text-muted small">Manager Review Date:</label>
                        <p class="mb-0">{{ appraisal.appraiserReviewDate || '-' }}</p>
                      </div>
                      <div class="col-md-3">
                        <div class="text-center bg-light p-3 rounded">
                          <h6 class="mb-3 text-success">Overall Ratings</h6>
                          <div class="row mb-2">
                            <div class="col-4">
                              <label class="text-muted small">Self</label>
                              <h6 class="text-primary mb-0">
                                {{ appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : '-' }}
                              </h6>
                            </div>
                            <div class="col-4">
                              <label class="text-muted small">Manager</label>
                              <h6 class="text-info mb-0">
                                {{ appraisal.sectionA_AppraiserOverallRating ? parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) : '-' }}
                              </h6>
                            </div>
                            <div class="col-4">
                              <label class="text-muted small">Final</label>
                              <h6 class="text-success mb-0">
                                {{ appraisal.finalOverallRating ? parseFloat(appraisal.finalOverallRating).toFixed(2) : '-' }}
                              </h6>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section A: Key Performance Areas -->
            <div class="row mb-4" v-if="kpas.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-target me-2 text-primary"></i>
                      Section A: Key Performance Areas
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered table-sm">
                        <thead class="table-light">
                        <tr>
                          <th width="3%">#</th>
                          <th width="18%">KPA</th>
                          <th width="18%">Results Achieved</th>
                          <th width="5%">Weight</th>
                          <th width="6%">Self</th>
                          <th width="6%">Manager</th>
                          <th width="44%">Manager Comments</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="kpa in kpas" :key="kpa.id">
                          <td class="text-center">{{ kpa.sNo }}</td>
                          <td><strong>{{ kpa.kpa }}</strong></td>
                          <td>{{ kpa.resultsAchieved || '-' }}</td>
                          <td class="text-center">{{ kpa.weightage }}%</td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ kpa.selfRating || '-' }}</span>
                          </td>
                          <td class="text-center">
                            <span v-if="kpa.appraiserRating" class="badge bg-success">{{ kpa.appraiserRating }}</span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td><small>{{ kpa.appraiserComment || '-' }}</small></td>
                        </tr>
                        </tbody>
                        <tfoot class="table-light">
                        <tr>
                          <td colspan="4" class="text-end"><strong>Section A Overall Rating:</strong></td>
                          <td class="text-center">
                              <span class="badge bg-primary">
                                {{ appraisal.sectionA_SelfOverallRating ? parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(2) : '-' }}
                              </span>
                          </td>
                          <td class="text-center">
                              <span v-if="appraisal.sectionA_AppraiserOverallRating" class="badge bg-success">
                                {{ parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(2) }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td></td>
                        </tr>
                        </tfoot>
                      </table>
                    </div>

                    <!-- Organizational Parameters -->
                    <div v-if="organizationalParams.length > 0" class="mt-4">
                      <h6 class="text-primary mb-3">
                        <i class="mdi mdi-help-circle-outline me-2"></i>
                        Key Organizational Parameters
                      </h6>
                      <div class="table-responsive">
                        <table class="table table-bordered table-sm">
                          <thead class="table-light">
                          <tr>
                            <th width="3%">#</th>
                            <th width="38%">Question</th>
                            <th width="10%">Employee's Answer</th>
                            <th width="10%">Manager Rating</th>
                            <th width="39%">Manager Comments</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="param in organizationalParams" :key="param.id">
                            <td class="text-center">{{ param.sNo }}</td>
                            <td><small>{{ param.question }}</small></td>
                            <td class="text-center">
                              <span class="badge bg-primary">{{ param.answer || '-' }}</span>
                            </td>
                            <td class="text-center">
                              <span v-if="param.appraiserRating" class="badge bg-success">{{ param.appraiserRating }}</span>
                              <span v-else class="text-muted">-</span>
                            </td>
                            <td><small>{{ param.appraiserComments || '-' }}</small></td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section B: Critical Success Attributes -->
            <div class="row mb-4" v-if="attributes.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-star me-2 text-primary"></i>
                      Section B: Critical Success Attributes
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered table-sm">
                        <thead class="table-light">
                        <tr>
                          <th width="3%">#</th>
                          <th width="28%">Attribute</th>
                          <th width="10%">Self</th>
                          <th width="10%">Manager</th>
                          <th width="49%">Manager Assessment</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="attr in attributes" :key="attr.id">
                          <td class="text-center">{{ attr.sNo }}</td>
                          <td><strong>{{ attr.attribute }}</strong></td>
                          <td class="text-center">
                            <span class="badge bg-primary">{{ attr.selfRating || '-' }}</span>
                          </td>
                          <td class="text-center">
                            <span v-if="attr.appraiserRating" class="badge bg-success">{{ attr.appraiserRating }}</span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td><small>{{ attr.appraiserAssessment || '-' }}</small></td>
                        </tr>
                        </tbody>
                        <tfoot class="table-light">
                        <tr>
                          <td colspan="2" class="text-end"><strong>Section B Overall Rating:</strong></td>
                          <td class="text-center">
                              <span class="badge bg-primary">
                                {{ appraisal.sectionB_SelfOverallRating ? parseFloat(appraisal.sectionB_SelfOverallRating).toFixed(2) : '-' }}
                              </span>
                          </td>
                          <td class="text-center">
                              <span v-if="appraisal.sectionB_AppraiserOverallRating" class="badge bg-success">
                                {{ parseFloat(appraisal.sectionB_AppraiserOverallRating).toFixed(2) }}
                              </span>
                            <span v-else class="text-muted">-</span>
                          </td>
                          <td></td>
                        </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section C: Employee Feedback -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-comment-text me-2 text-primary"></i>
                      Section C: Employee Feedback
                    </h5>
                  </div>
                  <div class="card-body">

                    <div class="row">
                      <!-- Job Content -->
                      <div class="col-md-6" v-if="jobContent.length > 0">
                        <h6 class="text-muted mb-3">Job Content</h6>
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered">
                            <thead class="table-light">
                            <tr>
                              <th>Attribute</th>
                              <th width="20%" class="text-center">Response</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="item in jobContent" :key="item.id">
                              <td><small>{{ item.attribute }}</small></td>
                              <td class="text-center">
                                  <span :class="['badge badge-sm', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                    {{ item.response }}
                                  </span>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <!-- Growth Opportunities -->
                      <div class="col-md-6" v-if="growthOpportunities.length > 0">
                        <h6 class="text-muted mb-3">Growth Opportunities</h6>
                        <div class="table-responsive">
                          <table class="table table-sm table-bordered">
                            <thead class="table-light">
                            <tr>
                              <th>Attribute</th>
                              <th width="20%" class="text-center">Response</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="item in growthOpportunities" :key="item.id">
                              <td><small>{{ item.attribute }}</small></td>
                              <td class="text-center">
                                  <span :class="['badge badge-sm', item.response === 'Yes' ? 'bg-success' : 'bg-secondary']">
                                    {{ item.response }}
                                  </span>
                              </td>
                            </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    <!-- Training Priorities -->
                    <div v-if="trainings.length > 0" class="mt-3">
                      <h6 class="text-muted mb-3">Training Priorities</h6>
                      <div class="row">
                        <div class="col-md-6" v-for="(training) in trainings" :key="training.id">
                          <div class="d-flex align-items-center mb-2">
                            <span class="badge bg-primary me-2">{{ training.priority }}</span>
                            <small>{{ training.trainingProgram }}</small>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Employee Comments -->
                    <div v-if="appraisal.associateComments" class="mt-3">
                      <h6 class="text-muted mb-2">Employee's Additional Comments</h6>
                      <div class="bg-light p-3 rounded">
                        <small>{{ appraisal.associateComments }}</small>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            </div>

            <!-- Section E: Goals for Next Period -->
            <div class="row mb-4" v-if="goals.length > 0">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light">
                    <h5 class="mb-0">
                      <i class="mdi mdi-flag-checkered me-2 text-primary"></i>
                      Section E: Goals & Action Plans for Next Period
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="table-responsive">
                      <table class="table table-bordered table-sm">
                        <thead class="table-light">
                        <tr>
                          <th width="5%">#</th>
                          <th width="75%">Action Plan</th>
                          <th width="20%">Target Date</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="goal in goals" :key="goal.id">
                          <td class="text-center">{{ goal.sNo }}</td>
                          <td>{{ goal.actionPlan }}</td>
                          <td class="text-center">{{ formatDate(goal.targetDate) }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- HR Comments (if exists) -->
            <div class="row mb-4" v-if="appraisal.reviewerComments">
              <div class="col-12">
                <div class="card border-warning">
                  <div class="card-header bg-warning">
                    <h5 class="mb-0">
                      <i class="mdi mdi-comment-alert me-2"></i>
                      HR/Reviewer Comments
                    </h5>
                  </div>
                  <div class="card-body">
                    <p class="mb-0">{{ appraisal.reviewerComments }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex justify-content-between">
                      <router-link to="/hr-dashboard" class="btn btn-secondary">
                        <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
                      </router-link>
                      <div>
                        <button
                            @click="reviewAppraisal"
                            class="btn btn-success"
                        >
                          <i class="mdi mdi-check-circle me-1"></i> Proceed to Final Review
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <!-- Not Found -->
          <div v-else class="text-center py-5">
            <i class="mdi mdi-alert-circle-outline display-4 text-muted"></i>
            <h5 class="mt-3">Appraisal Not Found</h5>
            <p class="text-muted">The appraisal you're looking for doesn't exist.</p>
            <router-link to="/hr-dashboard" class="btn btn-primary mt-3">
              <i class="mdi mdi-arrow-left me-1"></i> Back to Dashboard
            </router-link>
          </div>

        </div>
        <!-- end container-fluid -->
      </div>
      <!-- End Page-content -->
    </div>
    <!-- end content-minimize -->
  </div>
  <!-- end layout-wrapper -->
</template>

<script>
/* eslint-disable */
import { authUrl, execute } from "@/api";
import NavigationBar from "@/components/Navigation";

export default {
  name: "HRAppraisalView",

  components: {
    NavigationBar
  },

  data() {
    return {
      loading: false,
      appraisalId: null,
      appraisal: null,
      kpas: [],
      organizationalParams: [],
      attributes: [],
      jobContent: [],
      growthOpportunities: [],
      workCulture: [],
      colleagues: [],
      trainings: [],
      additionalFeedback: null,
      goals: []
    };
  },

  mounted() {
    this.appraisalId = this.$route.query.id;
    if (this.appraisalId) {
      this.loadAppraisal();
    }
  },

  methods: {
    async loadAppraisal() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAppraisalForHRReview");
        data.append("appraisalId", this.appraisalId);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          const details = res.data.data;

          this.appraisal = details.appraisal;
          this.kpas = details.kpas || [];
          this.organizationalParams = details.organizationalParams || [];
          this.attributes = details.attributes || [];
          this.jobContent = details.jobContent || [];
          this.growthOpportunities = details.growthOpportunities || [];
          this.workCulture = details.workCulture || [];
          this.colleagues = details.colleagues || [];
          this.trainings = details.trainings || [];
          this.additionalFeedback = details.additionalFeedback;
          this.goals = details.goals || [];
        } else {
          this.appraisal = null;
          this.$toast?.error("Failed to load appraisal") || alert("Failed to load appraisal");
        }
      } catch (error) {
        console.error("Failed to load appraisal:", error);
        this.$toast?.error("Error loading appraisal") || alert("Error loading appraisal");
      } finally {
        this.loading = false;
      }
    },

    reviewAppraisal() {
      this.$router.push({
        path: '/hr-review-form',
        query: { id: this.appraisalId }
      });
    },

    printAppraisal() {
      window.print();
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'bg-secondary',
        'Self-Assessment Complete': 'bg-info',
        'Appraiser Review Pending': 'bg-warning',
        'Appraiser Review Complete': 'bg-warning',
        'Reviewer Pending': 'bg-warning',
        'Reviewer Complete': 'bg-primary',
        'HR/CEO Review Pending': 'bg-primary',
        'Approved': 'bg-success',
        'Rejected': 'bg-danger'
      };
      return classes[status] || 'bg-secondary';
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
  }
};
</script>

<style scoped>
@media print {
  .page-title-box,
  .card-header button,
  .btn {
    display: none !important;
  }
}

.table > :not(caption) > * > * {
  padding: 0.5rem 0.5rem;
}

.badge {
  font-size: 0.875rem;
}

.badge-sm {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}
</style>